/**
 * ChangeReason.js
 * Created by calhosh on 10/22/2020.
 */

import React, {useState, useEffect, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {ChangeReasonModal} from '@ey/canvascoreservices';
import {DocumentChangeTypes} from '../../../util/uiconstants';
import {currentResource, labels} from '../../../util/utils';
import Utility from '../../../util/utilityfunctions';
import {clearErrors} from '../../../actions/erroractions';
import {setIsModalOpen} from '../../../actions/setismodalopen';
import {getAllDocumentExtensions} from '../../../actions/Document/documentextensionactions';
import EllipseControl from '../../Common/EllipsesControl/EllipsesControl';

const ChangeReason = (props) => {
	const {
		canvasDocument,
		show,
		modalSize,
		discardChangesLabel = labels.cancelButtonLabel,
		onHide,
		onConfirm,
		onReasonChanged,
		onBeforeReasonChanged
	} = props;

	const annotationMaxCharacters = 4000;

	const [isUpdatingReason, setIsUpdatingReason] = useState(false),
		[isLoadingComment, setIsLoadingComment] = useState(false),
		[showReasonChangeRequiredError, setShowReasonChangeRequiredError] = useState(false);

	const isAresView = useSelector((store) => store.isAresView),
		engagement = useSelector((store) => store.engagement),
		documentExtension = useSelector((state) => {
			if (state.documentExtensions && state.documentExtensions.length > 0) {
				state.documentExtensions?.find((de) => de.documentId?.toUpperCase() === canvasDocument.id?.toUpperCase());
			}
		});

	const dispatch = useDispatch();

	let isDocumentUpload = canvasDocument.id ? false : true;

	const onConfirmHandler = useCallback(
		(formState) => {
			const {documentChangeTypeId, comment} = formState;

			const promise = onConfirm(
				engagement.engagementId,
				canvasDocument.id,
				documentChangeTypeId,
				documentExtension?.id,
				normalize(comment),
				isReasonNonAdministrativeChanges(documentChangeTypeId)
			);

			if (promise instanceof Promise) {
				promise.then(() => {
					dispatch(setIsModalOpen(false));
					onChangeReasonModalHide(formState);

					if (onReasonChanged) {
						onReasonChanged({canvasDocument, selectedDocumentChangeTypeId: documentChangeTypeId, comment});
					}
				});
			}
			setIsUpdatingReason(false);
		},
		[dispatch, onConfirm, onReasonChanged, engagement, canvasDocument, documentExtension?.id, onChangeReasonModalHide]
	);

	const getAllExtensions = useCallback(() => {
		setIsLoadingComment(true);
		dispatch(getAllDocumentExtensions(engagement.engagementId, canvasDocument.id)).finally(() => {
			setIsLoadingComment(false);
		});
	}, [dispatch, engagement, canvasDocument]);

	function isReasonNonAdministrativeChanges(documentChangeTypeId) {
		return documentChangeTypeId === DocumentChangeTypes.NonAdministrativeChange;
	}

	function isCommentInputVisible(documentChangeTypeId) {
		return documentChangeTypeId === DocumentChangeTypes.NonAdministrativeChange;
	}

	const handleOnOkClick = useCallback(
		(formState) => {
			const {documentChangeTypeId} = formState;
			let promise;
			const reasonExists = documentChangeTypeId && documentChangeTypeId !== 0;

			setShowReasonChangeRequiredError(!reasonExists);

			if (reasonExists) {
				setIsUpdatingReason(true);

				if (onBeforeReasonChanged) {
					promise = onBeforeReasonChanged().then(() => {
						return onConfirmHandler(formState);
					});
				} else {
					promise = onConfirmHandler(formState);
				}

				return promise;
			}
		},
		[onBeforeReasonChanged, onConfirmHandler]
	);

	function normalize(text) {
		if (text) {
			return text.trim();
		}
		return text;
	}

	const onChangeReasonModalHide = useCallback(
		(formState) => {
			onHide && onHide();
			dispatch(setIsModalOpen(false));
		},
		[onHide]
	);

	const handleOnReasonChanged = useCallback((formState) => {
		const {documentChangeTypeId} = formState;
		setShowReasonChangeRequiredError(!documentChangeTypeId || documentChangeTypeId === 0);
	}, []);

	const titleHeader = (
		<EllipseControl
			content={isDocumentUpload ? labels.changeReasonUploadModalTitle : labels.changeReasonModalTitle}
			tooltip={isDocumentUpload ? labels.changeReasonUploadModalTitle : labels.changeReasonModalTitle}
			noOfLines={1}
		/>
	);

	useEffect(() => {
		if (show) {
			//This is required in order to not replicate a possible parent error notification on the child modal.
			dispatch(clearErrors());
			dispatch(setIsModalOpen(true));

			if (canvasDocument.id) {
				getAllExtensions();
			}
		}
	}, [show, canvasDocument.id, canvasDocument.documentChangeTypeId]);

	return (
		<ChangeReasonModal
			engagement={engagement}
			roleLabels={currentResource.roleForMember}
			annotationMaxCharacters={annotationMaxCharacters}
			charactersLabel={labels.characters}
			changeReasonModalAnnotationPlaceholder={''}
			changeReasonModalChangeReasonRequired={labels.changeReasonModalChangeReasonRequired}
			changeReasonUploadModalAnnotationText={labels.changeReasonUploadModalAnnotationText}
			changeReasonModalAnnotationText={labels.changeReasonModalAnnotationText}
			changeReasonUploadModalText={labels.changeReasonUploadModalText}
			changeReasonUploadModalTitle={labels.changeReasonUploadModalTitle}
			changeReasonModalTitle={labels.changeReasonModalTitle}
			/*comment={documentExtension?.changeReason}*/
			discardChangesLabel={discardChangesLabel}
			document={canvasDocument}
			documentChangeTypesOptions={currentResource.documentChangeTypesOptions}
			isLoadingComment={isLoadingComment}
			isUpdatingReason={isUpdatingReason}
			modalSize={modalSize}
			ofLabel={labels.of}
			onHide={onChangeReasonModalHide}
			onConfirm={handleOnOkClick}
			onReasonChanged={handleOnReasonChanged}
			parentContainerClassName={isAresView ? 'AresModal' : 'KronosModal'}
			reasonColumnTitle={labels.reasonColumnTitle}
			saveLabel={labels.saveLabel}
			setIsLoadingComment={setIsLoadingComment}
			show={show}
			showReasonChangeRequiredError={showReasonChangeRequiredError}
			showCommentInput={isCommentInputVisible}
			textAreaEditorDisabled={false}
			textAreaEditorShowCounter
			textAreaError={false}
			titleHeader={titleHeader}
			theme={Utility.getTheme()}
			textAreaEditorLabel={labels.changeReasonModalAnnotationPlaceHolder}
		/>
	);
};

export default ChangeReason;

ChangeReason.propTypes = {
	show: PropTypes.bool,
	modalSize: PropTypes.oneOf(['small', 'medium']),
	onHide: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired
};

const StyledChangeReasonModal = styled.section`
	.motif-modal-content.small {
		.motif-modal-body.ChangeReasonModal {
			height: var(--px-550);
		}
	}
	.ChangeReasonModal {
		.errorMessageWrapper {
			width: 100%;
		}
	}
	.customScrollbar {
		height: var(--px-650);
	}
	.ChangeReasonText,
	.EditReasonText {
		padding: var(--px-10) var(--px-20);
		font-size: var(--px-14);
	}
	.ChangeReasonSelect {
		padding: var(--px-10) var(--px-20);
		.react-select-container {
			.react-select__control {
				.react-select__value-container {
					&.react-select__value-container--has-value {
						padding: var(--px-6) var(--px-8);
					}
				}
			}
			input {
				line-height: var(--px-20);
			}
		}
	}
	.EditAnnotation {
		padding: var(--px-10) var(--px-20);
	}
`;
